
import { defineComponent, ref, reactive, onMounted } from 'vue';
import dayjs from 'dayjs';
import { message, Modal, Drawer } from 'ant-design-vue';
import { ReloadOutlined } from '@ant-design/icons-vue';
import { OioPagination, OioForm, OioFormItem, OioInput, OioButton, OioTag } from '@kunlun/vue-ui-antd';
import { BillingBill, DeliInvoiceInfo, PageQueryParams } from '../types';
import {
  queryIndependentBillingPage,
  querySellerIndependentBillingPage,
  queryInvoiceInfoList,
  querySellerInvoiceInfoList,
  refreshBillStatus,
  downloadInvoiceZip
} from '../api/billingBill';
import { useBillingStatus } from '../hooks/useBillingStatus';
import { useBillingAmount } from '../hooks/useBillingAmount';
import { usePortalType } from '../hooks/usePortalType';
import { INDEPENDENT_BILLING_STATUS_OPTIONS } from '../constants';
import BillingBillDetailPage from './BillingBillDetailPage.vue';
import IndependentBillingCreatePage from './IndependentBillingCreatePage.vue';

/**
 * 独立开票列表页。
 * 数据源按 portal 分流：品牌分销工作台走 seller 代理模型（后端叠加品牌数据权限），
 * 其余（分销运营中心/总部）走 platform 代理模型；两个代理模型的 queryPage 均已按
 * billCategory=INDEPENDENT 收口，故本页不会出现常规开票单。
 */
export default defineComponent({
  name: 'IndependentBillingListPage',
  components: {
    ReloadOutlined,
    Drawer,
    BillingBillDetailPage,
    IndependentBillingCreatePage,
    OioPagination,
    OioForm,
    OioFormItem,
    OioInput,
    OioButton,
    OioTag
  },
  setup() {
    const { getBillStatusText, getBillStatusColor, canDownloadInvoice } = useBillingStatus();
    const { formatAmount, getAmountClass } = useBillingAmount();
    const { isDistributor, isSeller } = usePortalType();

    const loading = ref(false);
    const tableData = ref<BillingBill[]>([]);
    const detailVisible = ref(false);
    const detailBillId = ref(0);
    const createVisible = ref(false);
    const tableRef = ref();

    /** 页内开票信息 ID → 开票信息记录，用于展示开票信息编码与抬头 */
    const invoiceInfoMap = reactive<Record<string, DeliInvoiceInfo>>({});

    const statusOptions = INDEPENDENT_BILLING_STATUS_OPTIONS;

    const searchForm = reactive({
      code: '',
      distributorName: '',
      distributorErpCodes: [] as string[],
      salesOrgCode: '',
      salesOrgName: '',
      statusList: [] as string[],
      createUserName: '',
      dateRange: null as any
    });

    const pagination = reactive({
      current: 1,
      pageSize: 15,
      total: 0
    });

    const buildRsql = (): string => {
      const conditions: string[] = ['(1==1)'];
      if (searchForm.code) conditions.push(`code=='${searchForm.code.trim()}'`);
      if (searchForm.distributorName) conditions.push(`distributorName=like='%${searchForm.distributorName}%'`);
      const erpCodes = (searchForm.distributorErpCodes || []).map((c) => c.trim()).filter((c) => !!c);
      if (erpCodes.length > 0) {
        conditions.push(`distributorErpCode=in=(${erpCodes.map((c) => `'${c}'`).join(',')})`);
      }
      if (searchForm.salesOrgCode) conditions.push(`salesOrgCode=='${searchForm.salesOrgCode.trim()}'`);
      if (searchForm.salesOrgName) conditions.push(`salesOrgName=like='%${searchForm.salesOrgName}%'`);
      if (searchForm.statusList.length > 0) {
        conditions.push(`status=in=(${searchForm.statusList.map((s) => `'${s}'`).join(',')})`);
      }
      if (searchForm.createUserName) conditions.push(`createUserName=like='%${searchForm.createUserName}%'`);
      if (searchForm.dateRange && searchForm.dateRange[0]) {
        conditions.push(`createDate=ge='${dayjs(searchForm.dateRange[0]).format('YYYY-MM-DD')} 00:00:00'`);
        conditions.push(`createDate=le='${dayjs(searchForm.dateRange[1]).format('YYYY-MM-DD')} 23:59:59'`);
      }
      return conditions.join(' and ');
    };

    /**
     * 批量补齐页内开票信息：BillingBill 只存 invoiceInfoId，编码与抬头需二次查询后前端映射。
     * invoiceInfoId 为空表示使用经销商档案默认开票信息，展示为「档案默认」。
     */
    const loadInvoiceInfoMap = async (bills: BillingBill[]) => {
      const ids = Array.from(
        new Set(bills.map((b) => b.invoiceInfoId).filter((id) => id != null) as number[])
      );
      if (ids.length === 0) return;
      const missing = ids.filter((id) => !invoiceInfoMap[String(id)]);
      if (missing.length === 0) return;
      try {
        const params: PageQueryParams = {
          currentPage: 1,
          size: missing.length,
          rsql: `id=in=(${missing.join(',')})`
        };
        const result: any = isSeller.value
          ? await querySellerInvoiceInfoList(params)
          : await queryInvoiceInfoList(params);
        (result?.content || []).forEach((info: DeliInvoiceInfo) => {
          invoiceInfoMap[String(info.id)] = info;
        });
      } catch (e: any) {
        // 失败提示由框架右上角 notification 统一处理，避免重复弹窗
      }
    };

    const getInvoiceInfoText = (row: BillingBill, field: 'code' | 'invoiceTitle') => {
      if (row.invoiceInfoId == null) return '档案默认';
      const info = invoiceInfoMap[String(row.invoiceInfoId)];
      return info ? (info as any)[field] || '-' : '-';
    };

    const fetchData = async () => {
      loading.value = true;
      try {
        const params: PageQueryParams = {
          currentPage: pagination.current,
          size: pagination.pageSize,
          rsql: buildRsql(),
          queryData: { sortBy: 'createDate', order: 'DESC' }
        };
        const result = isSeller.value
          ? await querySellerIndependentBillingPage(params)
          : await queryIndependentBillingPage(params);
        tableData.value = result.content || [];
        pagination.total = result.totalElements || 0;
        await loadInvoiceInfoMap(tableData.value);
      } catch (e: any) {
        // 失败提示由框架右上角 notification 统一处理，避免重复弹窗
      } finally {
        loading.value = false;
      }
    };

    const handleSearch = () => {
      pagination.current = 1;
      fetchData();
    };

    const handleReset = () => {
      searchForm.code = '';
      searchForm.distributorName = '';
      searchForm.distributorErpCodes = [];
      searchForm.salesOrgCode = '';
      searchForm.salesOrgName = '';
      searchForm.statusList = [];
      searchForm.createUserName = '';
      searchForm.dateRange = null;
      pagination.current = 1;
      fetchData();
    };

    const handlePaginationChange = (page: number, pageSize: number) => {
      pagination.current = page;
      pagination.pageSize = pageSize;
      fetchData();
    };

    const handleCreate = () => {
      createVisible.value = true;
    };

    const handleCreateSuccess = () => {
      createVisible.value = false;
      pagination.current = 1;
      fetchData();
    };

    const handleDetail = (record: BillingBill) => {
      detailBillId.value = record.id;
      detailVisible.value = true;
    };

    const handleDetailClose = () => {
      detailVisible.value = false;
      fetchData();
    };

    /** 刷新发票状态：触发后端按行重新查询 SAP 打包状态与大象发票 */
    const handleRefreshStatus = (record: BillingBill) => {
      Modal.confirm({
        title: '刷新发票状态',
        content: `确定要刷新独立开票包 ${record.code} 的发票状态吗？`,
        onOk: async () => {
          try {
            await refreshBillStatus(record.id);
            message.success('刷新指令已提交');
            fetchData();
          } catch (e: any) {
            // 失败提示由框架右上角 notification 统一处理，避免重复弹窗
          }
        }
      });
    };

    /** 下载发票：后端打包 zip 后返回下载地址 */
    const handleDownload = async (record: BillingBill) => {
      try {
        const url = await downloadInvoiceZip(record.id);
        if (url) {
          window.open(url, '_blank');
          message.success('发票打包下载已开始');
        } else {
          message.warning('未找到可下载的发票文件');
        }
      } catch (e: any) {
        // 失败提示由框架右上角 notification 统一处理，避免重复弹窗
      }
    };

    onMounted(() => {
      fetchData();
    });

    return {
      loading,
      tableData,
      tableRef,
      searchForm,
      pagination,
      statusOptions,
      detailVisible,
      detailBillId,
      createVisible,
      isDistributor,
      isSeller,
      getBillStatusText,
      getBillStatusColor,
      canDownloadInvoice,
      formatAmount,
      getAmountClass,
      getInvoiceInfoText,
      fetchData,
      handleSearch,
      handleReset,
      handlePaginationChange,
      handleCreate,
      handleCreateSuccess,
      handleDetail,
      handleDetailClose,
      handleRefreshStatus,
      handleDownload
    };
  }
});
